import { CommonFormDialog } from "@/components/common/CommonFormDialog";
import { CommonPagination } from "@/components/common/CommonPagination";
import Loading from "@/components/common/Loading";
import UpdateExchangeRateForm from "@/components/forms/UpdateExchangeRateForm";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { QUERY_KEY } from "@/constant/queryKey";
import { useDebounce } from "@/hooks/use-debounce";
import { usePagination } from "@/hooks/use-pagination";
import { useUrlParams } from "@/hooks/use-url-params";
import { CurrencyPaginationParams } from "@/services/api-service";
import {
  ADMIN_EXCHANGE_RATE_ENDPOINT,
  AdminExchangeRate,
} from "@/services/core/admin-exchange-rate.api";
import { useDialogStore } from "@/store/dialog-store";
import { format } from "date-fns";
import { Pencil, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { prettyFixedAmount } from "@/lib/utils";

const ExchangeRatePage = () => {
  const { params, updateParams } = useUrlParams<CurrencyPaginationParams>();
  const [searchInput, setSearchInput] = useState(params.q || "");
  const debouncedSearch = useDebounce(searchInput, 500);

  const [selectedExchangeRate, setSelectedExchangeRate] =
    useState<AdminExchangeRate | null>(null);
  const { openUpdateDialog, updateDialog, closeUpdateDialog } =
    useDialogStore();

  const {
    data: adminExchangeRates,
    isLoading,
    isError,
    meta,
    setPage,
    setSearch,
    setLimit,
  } = usePagination<AdminExchangeRate, CurrencyPaginationParams>({
    endpoint: `${ADMIN_EXCHANGE_RATE_ENDPOINT}`,
    queryKey: QUERY_KEY.ADMIN_EXCHANGE_RATE,
    initialParams: {
      currentPage: params.currentPage,
      pageSize: params.pageSize,
      s: params.s,
      q: params.q,
      based: params.based,
    },
  });

  const handlePageChange = (page: number) => {
    updateParams({ currentPage: page });
    setPage(page);
  };

  const handleLimitChange = (limit: number) => {
    updateParams({ pageSize: limit, currentPage: 1 });
    setLimit(limit);
  };

  // const handleSortChange = (sort: string) => {
  //   updateParams({ s: sort, currentPage: 1 });
  // };

  const handleSearch = (value: string) => {
    const trimmedValue = value.trim();
    setSearchInput(value);

    if (trimmedValue === params.q) {
      updateParams({ q: trimmedValue, currentPage: 1 });
    }
  };

  useEffect(() => {
    updateParams({ q: debouncedSearch, currentPage: 1 });
    setSearch(debouncedSearch || "");
  }, [debouncedSearch]);

  console.log("....", adminExchangeRates);

  const handleEdit = (rate: AdminExchangeRate) => {
    setSelectedExchangeRate(rate);
    openUpdateDialog();
  };

  return (
    <div className="w-full space-y-4">
      <div className="flex w-full items-center justify-between gap-4 sm:flex-row sm:items-center">
        <div className="relative w-1/2">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search exchange rate..."
            className="pl-8"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="text-muted-foreground hover:text-foreground absolute top-2.5 right-2 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
            >
              <X className="h-4 w-4 cursor-pointer" />
              <span className="sr-only">Clear search</span>
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : isError ? (
        <div className="py-8 text-center text-red-500">
          Error loading exchange rates
        </div>
      ) : adminExchangeRates.length > 0 ? (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>No.</TableHead>
                <TableHead>Base Currency Code</TableHead>
                <TableHead>Currency Code</TableHead>
                <TableHead className="text-right">Exchange Rate</TableHead>
                <TableHead className="text-right">Last Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {adminExchangeRates.map((rate, index) => (
                <TableRow key={rate.id}>
                  <TableCell className="font-medium">{index + 1}</TableCell>
                  <TableCell>{rate.basedCurrencyCode}</TableCell>
                  <TableCell>{rate.currencyCode}</TableCell>
                  <TableCell className="text-right">
                    {prettyFixedAmount(rate.amount)} {rate.currencyCode}
                  </TableCell>
                  <TableCell className="text-right">
                    {format(new Date(rate.updatedAt), "dd MMM yyyy HH:mm:ss")}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleEdit(rate)}
                        aria-label={`Edit ${rate.currencyCode}`}
                        className="cursor-pointer"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="flex h-42 items-center justify-center">
          <p>No exchange rates available</p>
        </div>
      )}
      {meta && (
        <CommonPagination
          page={meta.currentPage}
          totalPages={meta.totalPages}
          limit={meta.pageSize}
          hasNextPage={meta.hasNextPage}
          hasPrevPage={meta.hasPreviousPage}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          limitOptions={[10, 25, 50, 100]}
          showLimitSelector={adminExchangeRates.length > 10}
        />
      )}

      {/* Update Exchange Rate Dialog */}
      <CommonFormDialog
        title="Update Exchange Rate"
        description="Update the exchange rate for the selected currency"
        isOpen={updateDialog}
        onOpenChange={closeUpdateDialog}
      >
        {selectedExchangeRate && (
          <UpdateExchangeRateForm
            selectedExchangeRate={selectedExchangeRate}
            setSelectedExchangeRate={setSelectedExchangeRate}
            initialData={{
              id: selectedExchangeRate.id,
              amount: selectedExchangeRate.amount,
            }}
          />
        )}
      </CommonFormDialog>
    </div>
  );
};

export default ExchangeRatePage;
